import {
  SnowflakeAiObjects,
  snowflakeAiAgentAuthPolicyName,
  snowflakeAiMcpServerName,
  snowflakeAiSchemaName,
} from "@p0security/integrations-snowflake-ai-shared/constants";

const {
  agentAuthPolicy,
  connectorRole,
  connectorUser,
  database,
  mcpServer,
  mcpTool,
  schema,
  warehouse,
} = SnowflakeAiObjects;

// The Snowflake objects below follow Snowflake's documentation and have not
// been created in a live account yet; INT-617 runs this SQL and Terraform
// end to end. The tests check that both create the same objects and grants.

/** The MCP server's tools: one SQL tool that can write, with no warehouse
 * pinned, so it runs on the agent's own default warehouse. */
const mcpSpecification = `tools:
  - title: "SQL Execution Tool"
    name: "${mcpTool}"
    type: "SYSTEM_EXECUTE_SQL"
    description: "Execute SQL in this Snowflake account, as the agent's own Snowflake user."
    config:
      read_only: false`;

const indent = (text: string, spaces: number) =>
  text
    .split("\n")
    .map((line) => (line ? `${" ".repeat(spaces)}${line}` : line))
    .join("\n");

/** Generates the connector user's key pair: an unencrypted PKCS8 private key,
 * which the connector reads, and its public key, which Snowflake holds. */
export const keyPairCommand = [
  "openssl genrsa 2048 | openssl pkcs8 -topk8 -inform PEM -nocrypt -out rsa_key.p8",
  "openssl rsa -in rsa_key.p8 -pubout -out rsa_key.pub",
].join("\n");

/** The public key's body, as Snowflake's `RSA_PUBLIC_KEY` takes it. */
const publicKeyBodyCommand = "grep -v -- '-----' rsa_key.pub | tr -d '\\n'";

/** The setup as SQL, to run as ACCOUNTADMIN. */
export const setupSql = () => `USE ROLE ACCOUNTADMIN;

-- The connector's role. USERADMIN and SECURITYADMIN let it create each
-- agent's user and role, and grant them what a request names.
CREATE ROLE IF NOT EXISTS ${connectorRole};
GRANT ROLE USERADMIN TO ROLE ${connectorRole};
GRANT ROLE SECURITYADMIN TO ROLE ${connectorRole};

-- The small warehouse the connector lists roles, warehouses and objects with
CREATE WAREHOUSE IF NOT EXISTS ${warehouse}
  WAREHOUSE_SIZE = XSMALL
  AUTO_SUSPEND = 60
  AUTO_RESUME = TRUE
  INITIALLY_SUSPENDED = TRUE;
GRANT USAGE ON WAREHOUSE ${warehouse} TO ROLE ${connectorRole};

-- Lets the connector list every database, schema, table and view
GRANT IMPORTED PRIVILEGES ON DATABASE SNOWFLAKE TO ROLE ${connectorRole};

-- P0's database and schema, which hold the MCP server agents connect to
CREATE DATABASE IF NOT EXISTS ${database};
CREATE SCHEMA IF NOT EXISTS ${snowflakeAiSchemaName};
GRANT USAGE ON DATABASE ${database} TO ROLE ${connectorRole};
GRANT USAGE ON SCHEMA ${snowflakeAiSchemaName} TO ROLE ${connectorRole};

CREATE MCP SERVER IF NOT EXISTS ${snowflakeAiMcpServerName}
  FROM SPECIFICATION $$
${indent(mcpSpecification, 4)}
  $$;
GRANT USAGE ON MCP SERVER ${snowflakeAiMcpServerName} TO ROLE ${connectorRole};

-- Every agent's user gets this policy: token sign-in only, and a token
-- Snowflake accepts without a network policy
CREATE AUTHENTICATION POLICY IF NOT EXISTS ${snowflakeAiAgentAuthPolicyName}
  AUTHENTICATION_METHODS = ('PROGRAMMATIC_ACCESS_TOKEN')
  PAT_POLICY = (NETWORK_POLICY_EVALUATION = NOT_ENFORCED);
GRANT APPLY ON AUTHENTICATION POLICY ${snowflakeAiAgentAuthPolicyName} TO ROLE ${connectorRole};

-- The connector's user. Replace <public key> with the output of:
--   ${publicKeyBodyCommand}
CREATE USER IF NOT EXISTS ${connectorUser}
  TYPE = SERVICE
  DEFAULT_ROLE = ${connectorRole}
  DEFAULT_WAREHOUSE = ${warehouse}
  RSA_PUBLIC_KEY = '<public key>';
GRANT ROLE ${connectorRole} TO USER ${connectorUser};`;

/** `ORGNAME-ACCOUNTNAME`, split for the provider's settings. The item
 * validator guarantees exactly one `-`. */
const accountParts = (accountId: string) => {
  const [organization = "", ...account] = accountId.split("-");
  return { organization, account: account.join("-") };
};

/** The same setup on Snowflake's own Terraform provider.
 *
 * `snowflake_mcp_server` is stable from v2.21.0, so no preview feature is
 * enabled.
 */
export const setupTf = (accountId: string) => {
  const { organization, account } = accountParts(accountId);
  return `terraform {
  required_providers {
    snowflake = {
      source  = "snowflakedb/snowflake"
      version = ">= 2.21.0"
    }
  }
}

# Sign in as a user that can use ACCOUNTADMIN, e.g. with SNOWFLAKE_USER and
# SNOWFLAKE_PRIVATE_KEY in the environment.
provider "snowflake" {
  organization_name = "${organization}"
  account_name      = "${account}"
  role              = "ACCOUNTADMIN"
}

# The connector user's public key: the output of
#   ${publicKeyBodyCommand}
variable "p0_connector_public_key" {
  type = string
}

# The connector's role. USERADMIN and SECURITYADMIN let it create each agent's
# user and role, and grant them what a request names.
resource "snowflake_account_role" "p0_ai_connector" {
  name = "${connectorRole}"
}

resource "snowflake_grant_account_role" "p0_ai_connector_useradmin" {
  role_name        = "USERADMIN"
  parent_role_name = snowflake_account_role.p0_ai_connector.name
}

resource "snowflake_grant_account_role" "p0_ai_connector_securityadmin" {
  role_name        = "SECURITYADMIN"
  parent_role_name = snowflake_account_role.p0_ai_connector.name
}

# The small warehouse the connector lists roles, warehouses and objects with
resource "snowflake_warehouse" "p0_ai" {
  name                = "${warehouse}"
  warehouse_size      = "XSMALL"
  auto_suspend        = 60
  auto_resume         = "true"
  initially_suspended = true
}

resource "snowflake_grant_privileges_to_account_role" "p0_ai_warehouse" {
  account_role_name = snowflake_account_role.p0_ai_connector.name
  privileges        = ["USAGE"]
  on_account_object {
    object_type = "WAREHOUSE"
    object_name = snowflake_warehouse.p0_ai.name
  }
}

# Lets the connector list every database, schema, table and view
resource "snowflake_grant_privileges_to_account_role" "p0_ai_account_usage" {
  account_role_name = snowflake_account_role.p0_ai_connector.name
  privileges        = ["IMPORTED PRIVILEGES"]
  on_account_object {
    object_type = "DATABASE"
    object_name = "SNOWFLAKE"
  }
}

# P0's database and schema, which hold the MCP server agents connect to
resource "snowflake_database" "p0_ai" {
  name = "${database}"
}

resource "snowflake_schema" "p0_ai_mcp" {
  database = snowflake_database.p0_ai.name
  name     = "${schema}"
}

resource "snowflake_grant_privileges_to_account_role" "p0_ai_database" {
  account_role_name = snowflake_account_role.p0_ai_connector.name
  privileges        = ["USAGE"]
  on_account_object {
    object_type = "DATABASE"
    object_name = snowflake_database.p0_ai.name
  }
}

resource "snowflake_grant_privileges_to_account_role" "p0_ai_schema" {
  account_role_name = snowflake_account_role.p0_ai_connector.name
  privileges        = ["USAGE"]
  on_schema {
    schema_name = "${snowflakeAiSchemaName}"
  }

  depends_on = [snowflake_schema.p0_ai_mcp]
}

# ${snowflakeAiMcpServerName}
resource "snowflake_mcp_server" "p0_sql" {
  database      = snowflake_database.p0_ai.name
  schema        = snowflake_schema.p0_ai_mcp.name
  name          = "${mcpServer}"
  specification = <<-EOT
${indent(mcpSpecification, 4)}
  EOT
}

resource "snowflake_grant_privileges_to_account_role" "p0_ai_mcp_server" {
  account_role_name = snowflake_account_role.p0_ai_connector.name
  privileges        = ["USAGE"]
  on_schema_object {
    object_type = "MCP SERVER"
    object_name = "${snowflakeAiMcpServerName}"
  }

  depends_on = [snowflake_mcp_server.p0_sql]
}

# Every agent's user gets this policy: token sign-in only, and a token
# Snowflake accepts without a network policy. Snowflake won't drop it while an
# agent's user still has it.
resource "snowflake_authentication_policy" "p0_agent_auth" {
  database               = snowflake_database.p0_ai.name
  schema                 = snowflake_schema.p0_ai_mcp.name
  name                   = "${agentAuthPolicy}"
  authentication_methods = ["PROGRAMMATIC_ACCESS_TOKEN"]
  pat_policy {
    network_policy_evaluation = "NOT_ENFORCED"
  }
}

resource "snowflake_grant_privileges_to_account_role" "p0_ai_agent_auth" {
  account_role_name = snowflake_account_role.p0_ai_connector.name
  privileges        = ["APPLY"]
  on_schema_object {
    object_type = "AUTHENTICATION POLICY"
    object_name = "${snowflakeAiAgentAuthPolicyName}"
  }

  depends_on = [snowflake_authentication_policy.p0_agent_auth]
}

# The connector's user
resource "snowflake_service_user" "p0_ai_connector" {
  name              = "${connectorUser}"
  default_role      = snowflake_account_role.p0_ai_connector.name
  default_warehouse = snowflake_warehouse.p0_ai.name
  rsa_public_key    = var.p0_connector_public_key
}

resource "snowflake_grant_account_role" "p0_ai_connector_user" {
  role_name = snowflake_account_role.p0_ai_connector.name
  user_name = snowflake_service_user.p0_ai_connector.name
}
`;
};
